import { useEffect, useRef, useState } from "react";
import type { CropOffset } from "@/lib/imageCrop";

interface ImageCropFrameProps {
  imageUrl: string;
  aspectRatio: number;
  offset: CropOffset;
  onOffsetChange: (offset: CropOffset) => void;
}

export function ImageCropFrame({
  imageUrl,
  aspectRatio,
  offset,
  onOffsetChange,
}: ImageCropFrameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    startOffset: CropOffset;
    pointerId: number;
  } | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = imageUrl;
    return () => {
      img.onload = null;
    };
  }, [imageUrl]);

  const imgAspect = naturalSize ? naturalSize.w / naturalSize.h : aspectRatio;
  const overflowsHorizontally = imgAspect > aspectRatio;
  const overflowsVertically = imgAspect < aspectRatio;
  const isDraggable = overflowsHorizontally || overflowsVertically;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggable || !containerRef.current) return;
    e.preventDefault();
    containerRef.current.setPointerCapture(e.pointerId);
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffset: { ...offset },
      pointerId: e.pointerId,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId || !containerRef.current || !naturalSize) return;

    const rect = containerRef.current.getBoundingClientRect();
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    let newX = drag.startOffset.x;
    let newY = drag.startOffset.y;

    if (overflowsHorizontally) {
      const scaledImgWidth = (rect.height * naturalSize.w) / naturalSize.h;
      const overflow = scaledImgWidth - rect.width;
      if (overflow > 0) {
        newX = clamp01(drag.startOffset.x - dx / overflow);
      }
    }
    if (overflowsVertically) {
      const scaledImgHeight = (rect.width * naturalSize.h) / naturalSize.w;
      const overflow = scaledImgHeight - rect.height;
      if (overflow > 0) {
        newY = clamp01(drag.startOffset.y - dy / overflow);
      }
    }

    onOffsetChange({ x: newX, y: newY });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current?.pointerId === e.pointerId) {
      dragStateRef.current = null;
      if (containerRef.current?.hasPointerCapture(e.pointerId)) {
        containerRef.current.releasePointerCapture(e.pointerId);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className="relative w-full overflow-hidden rounded-lg bg-gray-900 touch-none select-none"
      style={{ aspectRatio: String(aspectRatio) }}
    >
      <img
        src={imageUrl}
        alt=""
        draggable={false}
        className="h-full w-full object-cover"
        style={{
          objectPosition: `${offset.x * 100}% ${offset.y * 100}%`,
          cursor: isDraggable ? "grab" : "default",
        }}
      />
      {isDraggable && (
        <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/40 to-transparent p-2">
          <span className="rounded-full bg-black/60 px-2 py-1 text-xs text-white">
            Vilkite, kad pakeistumėte kadrą
          </span>
        </div>
      )}
    </div>
  );
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
